// Hợp đồng của identity-service: dữ liệu vào lệnh/truy vấn (zod — gateway kiểm tra, service kiểm lại),
// kết quả trả về, claim JWT và sự kiện tích hợp.
import { z } from 'zod';
import type { AccountStatus, Grant } from '../context.js';
import type { TenantSettings } from './settings.js';
export * from './settings.js';

const uuid = z.string().uuid();
const text = (max = 200) => z.string().trim().max(max);

// ---------- Xác thực ----------
export const provisionTenantInput = z.object({
  slug: z.string().trim().toLowerCase().regex(/^[a-z0-9-]{3,40}$/, 'Mã công ty 3–40 ký tự a-z, 0-9, -'),
  ownerEmail: z.string().trim().toLowerCase().email(),
  ownerPassword: z.string().min(8, 'Mật khẩu tối thiểu 8 ký tự').max(200),
  ownerName: text().min(1),
});
export const loginInput = z.object({
  tenantSlug: z.string().trim().toLowerCase().min(1),
  email: z.string().trim().toLowerCase().email(),
  password: z.string().min(1).max(200),
  device: z.object({ ip: z.string().optional(), userAgent: z.string().optional() }).optional(),
});
export const refreshInput = z.object({ refreshToken: z.string().min(20), device: z.object({ ip: z.string().optional(), userAgent: z.string().optional() }).optional() });
export const changePasswordInput = z.object({ currentPassword: z.string().min(1), newPassword: z.string().min(8).max(200) });
export const registerUserInput = z.object({
  tenantSlug: z.string().trim().toLowerCase().min(1),
  email: z.string().trim().toLowerCase().email(),
  displayName: text().min(1),
  password: z.string().min(8).max(200),
  kind: z.enum(['member', 'guest']).default('member'),
});

// ---------- Đăng nhập một lần qua Elite Accounts (gateway đã xác minh OIDC + /api/ec/access trước khi gọi) ----------
const ssoIdentity = {
  tenantSlug: z.string().trim().toLowerCase().min(1),
  issuer: z.string().url().max(300),
  subject: uuid,
};
export const ssoLoginInput = z.object({
  ...ssoIdentity,
  email: z.string().trim().toLowerCase().email(),
  name: text().default(''),
  sessionVersion: z.number().int().min(1),
  device: z.object({ ip: z.string().optional(), userAgent: z.string().optional() }).optional(),
});
export const ssoRevokeInput = z.object({
  ...ssoIdentity,
  reason: z.enum(['sso_revoked', 'sso_version_changed']),
  /** Chỉ thu hồi phiên có sessionVersion nhỏ hơn số này (đổi phiên bản: phiên đăng nhập lại sau đó vẫn giữ). */
  belowVersion: z.number().int().min(1).optional(),
});
/**
 * Lệnh vận hành một lần (không có HTTP): tạo công ty dùng SSO đã thiết lập sẵn (tên, màu, logo) + chỉ định email chủ sở hữu,
 * không mật khẩu. Chủ sở hữu đăng nhập SSO lần đầu được cấp hồ sơ quản trị tự động — không qua màn "Khai báo công ty".
 */
export const provisionSsoTenantInput = z.object({
  slug: provisionTenantInput.shape.slug,
  ownerEmail: z.string().trim().toLowerCase().email(),
  ownerName: text().min(1),
  companyName: text().min(1, 'Nhập tên công ty'),
  brand: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default('#2F89C9'),
  brand2: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default('#414042'),
  logoUrl: z.string().url().max(500).regex(/^https:\/\//, 'Logo phải là URL https').optional(),
});

// ---------- Đồng bộ định kỳ với Elite Accounts (docs/work/2026-10-25-accounts-cathouse-lien-thong/spec-gd1.md) ----------
/** Một người có grant của app trên Accounts — khớp `AppMember` của `GET /api/ec/apps/{appId}/members` (accounts/openapi.yaml). */
export const accountsMember = z.object({
  /** Mã bất biến của Accounts (sub OIDC). Text tự do phía Accounts; chỉ sub dạng uuid mới liên kết được ở Cathouse. */
  subject: z.string().min(1).max(1024),
  email: z.string().trim().toLowerCase().email().max(320),
  emailVerified: z.boolean(),
  name: z.string().max(500),
  fullName: z.string().max(500).nullable(),
  employeeCode: z.string().max(100).nullable(),
  jobTitle: z.string().max(500).nullable(),
  department: z.object({ id: z.string().min(1).max(100), name: z.string().max(500) }).nullable(),
  managerSubject: z.string().min(1).max(1024).nullable(),
  status: z.enum(['active', 'on_leave', 'suspended', 'departed']).nullable(),
  disabled: z.boolean(),
  accessActive: z.boolean(),
  grant: z.object({ role: z.string().min(1).max(100), scope: z.enum(['self', 'team', 'all']) }),
  sessionVersion: z.number().int().min(1),
  updatedAt: z.string().datetime({ offset: true }),
});
export type AccountsMember = z.infer<typeof accountsMember>;
export const accountsMembersPage = z.object({
  appId: z.string().min(1),
  observedAt: z.string().datetime({ offset: true }),
  members: z.array(accountsMember).max(500),
  nextCursor: z.string().min(1).max(8192).nullable(),
});
export type AccountsMembersPage = z.infer<typeof accountsMembersPage>;
export const runAccountsSyncInput = z.object({});
export const confirmAccountsSyncInput = z.object({ runId: uuid });

export type AccountsSyncMode = 'dry-run' | 'apply';
export type AccountsSyncRunStatus = 'ok' | 'failed' | 'halted';
export type AccountsOffboardReason = 'departed' | 'suspended' | 'disabled' | 'grant_removed';
/** Hành động trong kế hoạch một lượt (lưu ở accounts_sync_runs.plan — không có email). */
export interface AccountsSyncAction {
  /** withdraw_join: rút yêu cầu tham gia do đồng bộ tạo sẵn khi người đó đã nghỉ / bị khoá / mất quyền trước khi được duyệt. */
  kind: 'profile' | 'join' | 'offboard' | 'clear' | 'withdraw_join';
  subject: string;
  name: string;
  userId: string | null;
  staffId: string | null;
  reason?: AccountsOffboardReason;
  /** Trường hồ sơ sẽ đổi (chỉ tên trường, không giá trị). */
  fields?: string[];
}
export type AccountsSyncIssueKind = 'missing_in_cathouse' | 'missing_in_accounts' | 'status_mismatch' | 'still_active_in_accounts' | 'email_conflict' | 'email_invalid' | 'no_access';
export interface AccountsSyncIssue {
  kind: AccountsSyncIssueKind;
  subject: string | null;
  userId: string | null;
  staffId: string | null;
  name: string;
  /**
   * Mã chi tiết: missing_in_cathouse ⇒ join_created | join_pending | same_email_unlinked | not_created | not_eligible;
   * missing_in_accounts ⇒ unlinked | absent | owner | pending_join; status_mismatch ⇒ lý do rời đi | owner | pending_join (chưa có tài khoản, yêu cầu tham gia đang chờ); still_active_in_accounts ⇒ left | account_left;
   * email_invalid ⇒ unverified | domain (email Accounts chưa xác minh / ngoài miền công ty: không ghi vào Cathouse); no_access ⇒ unverified | inactive.
   */
  detail: string;
  /** Trang quản trị Accounts của người đó (khi có subject). */
  accountsUrl: string | null;
}
export interface AccountsSyncRunView {
  runId: string;
  trigger: 'timer' | 'manual' | 'confirm';
  mode: AccountsSyncMode;
  status: AccountsSyncRunStatus;
  startedAt: string;
  finishedAt: string | null;
  memberCount: number | null;
  summary: Record<string, number>;
  errorCode: string | null;
  /** Lượt halted: lý do dừng (TOO_MANY_OFFBOARD, EMPTY_SNAPSHOT). */
  haltReasons: string[];
  confirmedAt: string | null;
}
export interface AccountsSyncStatus {
  enabled: boolean;
  mode: AccountsSyncMode;
  lastRun: AccountsSyncRunView | null;
  lastOk: AccountsSyncRunView | null;
  /** Lượt mới nhất đang halted chờ duyệt. */
  halted: { runId: string; reasons: string[]; proposed: AccountsSyncAction[] } | null;
  /** Hành động của lượt gần nhất không lỗi (dry-run: "Sẽ làm"; apply: đã làm). */
  actions: AccountsSyncAction[];
  issues: AccountsSyncIssue[];
}
/** aggregateId = staffId. Accounts là nguồn họ tên / email / chức danh của hồ sơ HR đã liên kết. */
export interface AccountsProfileSynced {
  staffId: string;
  userId: string;
  /** Họ tên HR (fullName của Accounts, không có thì name). */
  name: string;
  /** null = email Accounts chưa xác minh / ngoài miền công ty ⇒ hr giữ email hiện có. */
  email: string | null;
  accountsEmployeeCode: string | null;
  /** null = Accounts để trống (hr giữ chức danh hiện có). */
  jobTitle: string | null;
  department: { id: string; name: string } | null;
  managerStaffId: string | null;
  accountsStatus: 'active' | 'on_leave' | 'suspended' | 'departed' | null;
  syncedAt: string;
}
/** aggregateId = staffId. Một lần cho mỗi đợt (lặp lại không phát thêm). */
export interface AccountsOffboardingSuggested {
  staffId: string;
  userId: string;
  reason: AccountsOffboardReason;
  accountsStatus: 'active' | 'on_leave' | 'suspended' | 'departed' | null;
  detectedAt: string;
}
/** aggregateId = staffId. Người đó có lại quyền ở Accounts ⇒ rút đề xuất. */
export interface AccountsOffboardingCleared {
  staffId: string;
  userId: string;
}
/** aggregateId = runId. halted: chốt an toàn dừng lượt; failing: lỗi liên tiếp; recovered: lượt thành công sau đó. */
export interface AccountsSyncAlert {
  runId: string;
  reason: string;
  proposedCount: number;
}
/** aggregateId = userId. Tên hiển thị / email của người dùng đổi (đăng nhập SSO hoặc đồng bộ Accounts). Không mang email. */
export interface UserProfileUpdated {
  userId: string;
  displayName: string;
  emailChanged: boolean;
  source: 'sso_login' | 'accounts_sync';
}

export interface AuthTokens {
  accessToken: string;
  expiresIn: number; // giây
  refreshToken: string;
  refreshExpiresAt: string;
  sessionId: string;
}

/** Claim của access token (RS256). Quyền rút gọn: o/t/a = own/team/all, 1 = có. */
export interface AccessClaims {
  iss: 'cathouse-identity';
  aud: 'cathouse-gateway';
  sub: string; // userId
  tid: string; // tenantId
  sid: string; // sessionId
  stf: string | null; // staffId
  rid: string | null; // roleId
  ast: AccountStatus | 'none';
  pv: number;
  tier: 'admin' | 'interact';
  perms: Record<string, 'o' | 't' | 'a' | 1>;
  mods: number; // phiên bản cài đặt
  /** Phiên đăng nhập qua Elite Accounts: sub + sessionVersion lúc đăng nhập (gateway kiểm lại Accounts). */
  sso?: { sub: string; v: number };
  iat?: number;
  exp?: number;
  jti?: string;
}

// ---------- Tenant & cài đặt ----------
export const completeSetupInput = z.object({
  companyName: text().min(1, 'Nhập tên công ty'),
  brand: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default('#2F89C9'),
  brand2: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default('#414042'),
  owner: z.object({ fullName: text().min(1, 'Nhập họ tên'), title: text().default('Giám đốc'), phone: z.string().optional() }),
});

// ---------- Tài khoản ----------
export const staffRef = z.object({ staffId: uuid });
export const assignRoleInput = z.object({ staffId: uuid, roleId: z.string().min(1) });
export const lockAccountInput = z.object({ staffId: uuid, reason: z.string().trim() });
export const linkUserInput = z.object({ staffId: uuid, userId: uuid });
export const listAccountsInput = z.object({
  status: z.enum(['all', 'active', 'unlinked', 'locked', 'left']).default('all'),
  roleId: z.string().optional(),
  q: z.string().trim().optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(500).default(100),
});
export const eligibleStaffInput = z.object({ permission: z.string().min(1), excludeStaffId: uuid.optional() });

export type AccountDisplayStatus = 'active' | 'unlinked' | 'locked' | 'left';
export interface AccountRow {
  staffId: string;
  name: string;
  code: string | null;
  title: string | null;
  phone: string | null;
  teamId: string | null;
  teamName: string | null;
  userId: string | null;
  loginName: string | null;
  loginEmail: string | null;
  roleId: string | null;
  roleName: string;
  accessTier: 'admin' | 'interact';
  displayStatus: AccountDisplayStatus;
  lastSeenAt: string | null;
  hasDanglingRole: boolean;
  lockReason: string | null;
  lockedAt: string | null;
  lockedByStaffId: string | null;
  version: number;
}
export interface AccountList {
  rows: AccountRow[];
  total: number;
  counts: Record<'all' | AccountDisplayStatus, number>;
}

// ---------- Vai trò ----------
const permsMap = z.record(z.string(), z.union([z.literal(true), z.enum(['own', 'team', 'all'])]));
export const createRoleInput = z.object({ copyFromRoleId: z.string().min(1), name: text().optional(), description: text(500).optional() });
export const updateRoleInput = z.object({ roleId: z.string().min(1), expectedVersion: z.number().int().min(0), name: text(), description: text(500), perms: permsMap });
export const roleRef = z.object({ roleId: z.string().min(1) });

export interface RoleView {
  id: string;
  name: string;
  shortName: string;
  description: string;
  system: boolean;
  custom: boolean;
  hasOverride: boolean;
  perms: '*' | Record<string, Grant>;
  accessTier: 'admin' | 'interact';
  activeUserCount: number;
  version: number;
  editable: boolean;
}

// ---------- Yêu cầu tham gia ----------
export const submitJoinRequestInput = z.object({ fullName: text().min(1, 'Nhập họ tên'), phone: z.string().optional(), note: text(500).optional() });
export const decideJoinRequestInput = z.object({ joinRequestId: uuid, roleId: z.string().min(1).optional() });
export interface JoinRequestView {
  id: string;
  userId: string;
  fullName: string;
  phone: string | null;
  note: string | null;
  status: 'pending' | 'approved' | 'fulfilled' | 'rejected';
  submittedAt: string;
}

// ---------- Bootstrap ----------
/** provisioning = đã duyệt/thiết lập, đang chờ hr-service tạo hồ sơ nhân sự. */
export type Gate = 'setup' | 'setup_forbidden' | 'provisioning' | 'owner_recovery' | 'join' | 'join_pending' | 'locked' | 'ready';
export interface Bootstrap {
  user: { id: string; email: string; displayName: string; kind: 'member' | 'guest' };
  /** logoUrl: logo cho nền sáng; logoDarkUrl: bản cho nền tối (trắng), null ⇒ chưa có. */
  tenant: { id: string; slug: string; name: string; brand: string; brand2: string; logoUrl: string | null; logoDarkUrl: string | null; status: 'pending_setup' | 'active' | 'suspended'; isOwner: boolean };
  gate: Gate;
  account: { staffId: string; status: AccountStatus; lockReason: string | null; name: string; code: string | null; title: string | null; teamId: string | null } | null;
  role: { id: string; name: string; shortName: string } | null;
  grants: Record<string, Grant>;
  team: string[];
  accessTier: 'admin' | 'interact';
  enabledModules: string[];
  visibleModules: string[];
  /** Cài đặt hiệu lực; profile.logo / logoDark luôn null ở đây (logo đọc từ tenant.logoUrl / logoDarkUrl). */
  settings: TenantSettings;
  settingsVersion: number;
  pv: number;
  joinRequest: JoinRequestView | null;
}

/** Snapshot quyền gateway đọc ở Redis `authz:{tenantId}:{staffId}`. */
export interface AuthzSnapshot {
  tenantId: string;
  staffId: string;
  userId: string | null;
  roleId: string | null;
  status: AccountStatus;
  lockReason: string | null;
  pv: number;
  grants: Record<string, Grant>;
  team: string[];
  accessTier: 'admin' | 'interact';
  isSeller: boolean;
}

export const authzKey = (tenantId: string, staffId: string) => `authz:${tenantId}:${staffId}`;
export const userStaffKey = (tenantId: string, userId: string) => `authz:user:${tenantId}:${userId}`;
export const settingsKey = (tenantId: string) => `tenant:${tenantId}:settings`;
export const sessionDenyKey = (sessionId: string) => `session:deny:${sessionId}`;

// ---------- Sự kiện ----------
export const EVENTS = {
  tenantProvisioned: 'identity.tenant.provisioned',
  tenantSetupCompleted: 'identity.tenant.setupCompleted',
  tenantSettingsUpdated: 'identity.tenantSettings.updated',
  userRegistered: 'identity.user.registered',
  userPasswordChanged: 'identity.user.passwordChanged',
  userDeactivated: 'identity.user.deactivated',
  userReactivated: 'identity.user.reactivated',
  sessionStarted: 'identity.session.started',
  sessionRevoked: 'identity.session.revoked',
  loginFailed: 'identity.loginAttempt.failed',
  accountCreated: 'identity.account.created',
  accountLinked: 'identity.account.linked',
  accountUnlinked: 'identity.account.unlinked',
  accountRoleAssigned: 'identity.account.roleAssigned',
  accountLocked: 'identity.account.locked',
  accountUnlocked: 'identity.account.unlocked',
  accountDeactivated: 'identity.account.deactivated',
  accountReactivated: 'identity.account.reactivated',
  permissionsChanged: 'identity.account.permissionsChanged',
  roleCreated: 'identity.role.created',
  roleUpdated: 'identity.role.updated',
  roleReset: 'identity.role.reset',
  roleDeleted: 'identity.role.deleted',
  joinSubmitted: 'identity.joinRequest.submitted',
  joinApproved: 'identity.joinRequest.approved',
  joinRejected: 'identity.joinRequest.rejected',
  joinFulfilled: 'identity.joinRequest.fulfilled',
  provisioningRequested: 'identity.employeeProvisioning.requested',
  provisioningStalled: 'identity.employeeProvisioning.stalled',
  permissionCatalogUpdated: 'identity.permissionCatalog.updated',
  userProfileUpdated: 'identity.user.profileUpdated',
  accountsProfileSynced: 'identity.accountsSync.profileSynced',
  accountsOffboardingSuggested: 'identity.accountsSync.offboardingSuggested',
  accountsOffboardingCleared: 'identity.accountsSync.offboardingCleared',
  accountsSyncHalted: 'identity.accountsSync.halted',
  accountsSyncFailing: 'identity.accountsSync.failing',
  accountsSyncRecovered: 'identity.accountsSync.recovered',
} as const;

export interface TenantSettingsUpdated {
  settingsVersion: number;
  changedSections: string[];
  settings: TenantSettings;
  enabledModules: string[];
}
export interface PermissionsChanged {
  staffId: string;
  userId: string | null;
  roleId: string | null;
  status: AccountStatus;
  authzVersion: number;
  grants: Record<string, Grant>;
  accessTier: 'admin' | 'interact';
  isSeller: boolean;
}
export interface EmployeeProvisioningRequested {
  provisioningId: string;
  sourceRef: { type: 'tenantSetup' | 'ownerRecovery' | 'joinRequest'; id: string };
  draft: { fullName: string; phone: string | null; title: string; contractType: 'official' | 'probation' | 'ctv'; joinDate: string; teamId: null; target: 0; suggestedCode?: string };
  access: { userId: string; roleId: string };
}
/**
 * Job provisioning-watchdog: yêu cầu cấp hồ sơ còn chờ hr quá IDENTITY_PROVISIONING_STALL_MS (mặc định 5 phút) — phát đúng một lần
 * mỗi yêu cầu; yêu cầu vẫn tự hoàn tất nếu hr.employee.registered đến muộn. aggregateId = provisioningId.
 */
export interface EmployeeProvisioningStalled {
  provisioningId: string;
  sourceRef: EmployeeProvisioningRequested['sourceRef'];
  requestedAt: string;
  /** Họ tên trên bản nháp (hiển thị "Chưa tạo được hồ sơ nhân sự cho {họ tên}"). */
  draft?: { fullName: string };
}
/** aggregateId = joinRequestId. phone đã normPhone. */
export interface JoinRequestSubmitted {
  joinRequestId: string;
  userId: string;
  fullName: string;
  phone: string | null;
  note: string | null;
  submittedAt: string;
}
export interface JoinRequestApproved {
  joinRequestId: string;
  roleId: string;
  approvedByStaffId: string | null;
}
export interface JoinRequestRejected {
  joinRequestId: string;
  fullName: string;
  rejectedByStaffId: string | null;
}
/** Yêu cầu đã liên kết tài khoản (hr phản hồi cấp hồ sơ, hoặc LinkUser khi người dùng còn yêu cầu mở). */
export interface JoinRequestFulfilled {
  joinRequestId: string;
  staffId: string;
  userId: string;
}

export type EventType = (typeof EVENTS)[keyof typeof EVENTS];
/**
 * Bản đồ loại sự kiện ⇒ payload cho các loại đã khai báo interface (consumer: IntegrationEvent<identity.EventPayloads['identity.joinRequest.submitted']>).
 * Chưa đủ mọi EVENTS: payload tài khoản / phiên / vai trò còn theo đặc tả identity §5.
 */
export interface EventPayloads {
  [EVENTS.tenantSettingsUpdated]: TenantSettingsUpdated;
  [EVENTS.permissionsChanged]: PermissionsChanged;
  [EVENTS.joinSubmitted]: JoinRequestSubmitted;
  [EVENTS.joinApproved]: JoinRequestApproved;
  [EVENTS.joinRejected]: JoinRequestRejected;
  [EVENTS.joinFulfilled]: JoinRequestFulfilled;
  [EVENTS.provisioningRequested]: EmployeeProvisioningRequested;
  [EVENTS.provisioningStalled]: EmployeeProvisioningStalled;
  [EVENTS.userProfileUpdated]: UserProfileUpdated;
  [EVENTS.accountsProfileSynced]: AccountsProfileSynced;
  [EVENTS.accountsOffboardingSuggested]: AccountsOffboardingSuggested;
  [EVENTS.accountsOffboardingCleared]: AccountsOffboardingCleared;
  [EVENTS.accountsSyncHalted]: AccountsSyncAlert;
  [EVENTS.accountsSyncFailing]: AccountsSyncAlert;
  [EVENTS.accountsSyncRecovered]: AccountsSyncAlert;
}
